/**
 * Pickup — a lightweight, independently-schedulable warehouse run (D-1, FMS
 * feedback 2026-08-11). Instead of a task of kind='pickup', a pickup is its own
 * object: ONE trip that can cover N projects' orders ("one trip, four different
 * orders", 28:01). It links those projects and carries ordered `stops` (the
 * warehouses / addresses to hit, in sequence).
 *
 * The pickup owns NO line items: its checklist is the linked projects'
 * packing-slip `project_line_items` — the crew check off `received`, and a
 * project infers "inventory picked up" once its non-backordered packing-slip
 * lines are received. Crew scheduling comes free via the shared assignment
 * block (`pickup_id` anchor, A1). Stops reuse the pickup-task stop shape
 * verbatim (`PickupLocationSchema`), so the supervisor editor and crew journey
 * map work unchanged.
 */
import { z } from 'zod';
import { UuidSchema, IsoDateTimeSchema } from './common.js';
import { PickupStatusSchema } from './enums.js';
import { PickupLocationSchema, TaskAssignmentInputSchema } from './tasks.js';
import { ProjectLineItemSchema } from './line-items.js';
import { BlockWarningSchema } from './project-assignments.js';
/** A pickup's ordered stops reuse the pickup-task stop shape verbatim. */
export const PickupStopSchema = PickupLocationSchema;
/**
 * A pickup row as the API returns it. The linked projects (with their
 * packing-list checklist + "picked up" rollup) and the assigned crew ride on
 * the richer detail response built in the routes (C2) — this is the base shape.
 */
export const PickupSchema = z.object({
    id: UuidSchema,
    status: PickupStatusSchema,
    /** Optional label; when null the UI derives one from the stops. */
    title: z.string().max(200).nullable(),
    scheduled_start: IsoDateTimeSchema.nullable(),
    scheduled_end: IsoDateTimeSchema.nullable(),
    stops: z.array(PickupStopSchema).default([]),
    notes: z.string().max(2000).nullable(),
    /** Set when the crew Start / Finish the run (parity with a project). */
    started_at: IsoDateTimeSchema.nullable(),
    finished_at: IsoDateTimeSchema.nullable(),
    created_at: IsoDateTimeSchema,
    updated_at: IsoDateTimeSchema,
});
/** Create a pickup: at least one project, ordered stops, an optional window. */
export const PickupCreateSchema = z.object({
    title: z.string().max(200).nullable().optional(),
    scheduled_start: IsoDateTimeSchema.nullable().optional(),
    scheduled_end: IsoDateTimeSchema.nullable().optional(),
    stops: z.array(PickupStopSchema).default([]),
    notes: z.string().max(2000).nullable().optional(),
    /** The projects this one trip services (their packing lists become the checklist). */
    project_ids: z.array(UuidSchema).min(1),
});
/**
 * Crew Start / Finish on a pickup (C3): the assigned crew move it
 * scheduled → in_progress (Start) → complete (Finish), and can undo a Finish
 * (complete → in_progress). `cancelled` stays supervisor-only via PATCH.
 */
export const PickupTransitionSchema = z.object({
    to: z.enum(['scheduled', 'in_progress', 'complete']),
});
/** Update a pickup: any subset. `project_ids`, when present, REPLACES the link set. */
export const PickupUpdateSchema = z.object({
    title: z.string().max(200).nullable().optional(),
    status: PickupStatusSchema.optional(),
    scheduled_start: IsoDateTimeSchema.nullable().optional(),
    scheduled_end: IsoDateTimeSchema.nullable().optional(),
    stops: z.array(PickupStopSchema).optional(),
    notes: z.string().max(2000).nullable().optional(),
    project_ids: z.array(UuidSchema).min(1).optional(),
});
// ---------------------------------------------------------------------------
// Read shapes — the pickup detail + list responses (C2)
// ---------------------------------------------------------------------------
/** Packing-list rollup for one linked project — drives "inventory picked up". */
export const PickupPackingRollupSchema = z.object({
    /** Packing-slip lines on the project. */
    total: z.number().int(),
    /** Of those, checked off `received`. */
    received: z.number().int(),
    /** Ordered but not fully shipped — can't be picked up, excluded from "done". */
    backordered: z.number().int(),
});
/** One project a pickup services: its packing checklist + picked-up rollup. */
export const PickupProjectSummarySchema = z.object({
    project_id: UuidSchema,
    title: z.string(),
    client_name: z.string().nullable(),
    site_short_address: z.string().nullable(),
    /** Derived: every non-backordered packing-slip line received (and there is ≥1). */
    picked_up: z.boolean(),
    packing: PickupPackingRollupSchema,
    /** The project's packing-slip line items — the crew's check-off list. */
    line_items: z.array(ProjectLineItemSchema),
});
/** A crew member on the pickup (from its pickup-anchored blocks). */
export const PickupCrewMemberSchema = z.object({
    crew_member_id: UuidSchema,
    user_id: UuidSchema,
    display_name: z.string(),
    avatar_url: z.string().nullable(),
    crew_role: z.string(),
    is_lead: z.boolean(),
});
/** Full pickup detail: the row + a display title + linked projects + crew. */
export const PickupDetailSchema = PickupSchema.extend({
    /** `title` when set, else derived from the stops (e.g. "Pickup · Northlake +1"). */
    display_title: z.string(),
    projects: z.array(PickupProjectSummarySchema),
    crew: z.array(PickupCrewMemberSchema),
});
/** A row in the supervisor pickups list. */
export const PickupListItemSchema = PickupSchema.extend({
    display_title: z.string(),
    project_count: z.number().int(),
    crew_count: z.number().int(),
    /** Every linked project picked up — the whole run is done. */
    all_picked_up: z.boolean(),
});
export const PickupListSchema = z.object({ data: z.array(PickupListItemSchema) });
// ---------------------------------------------------------------------------
// Crew assignment (C2b) — mirrors project assignment, but one block per crew
// spanning the pickup's single window (no per-day fan-out).
// ---------------------------------------------------------------------------
/** A crew member on the pickup + the block window they hold. */
export const PickupAssignmentSchema = PickupCrewMemberSchema.extend({
    scheduled_start: IsoDateTimeSchema.nullable(),
    scheduled_end: IsoDateTimeSchema.nullable(),
});
export const PickupAssignmentsSchema = z.object({
    pickup_id: UuidSchema,
    assignments: z.array(PickupAssignmentSchema),
    /** Soft pickup-overlap warnings from the last set/reassign (D-11 warn-not-block). */
    warnings: z.array(BlockWarningSchema).default([]),
});
/** Set the full crew set on a pickup at once (reuses the task/project input row). */
export const PickupAssignmentsSetSchema = z.object({
    assignments: z.array(TaskAssignmentInputSchema).max(20),
});
/** Move one crew member on/off a pickup (dispatch-board drag). */
export const PickupReassignSchema = z
    .object({
    from_crew_member_id: UuidSchema.nullable().optional(),
    to_crew_member_id: UuidSchema.nullable().optional(),
})
    .refine((v) => v.from_crew_member_id || v.to_crew_member_id, {
    message: 'At least one of from_crew_member_id or to_crew_member_id is required',
});
//# sourceMappingURL=pickups.js.map